// Other Settings → Document Categories: the document types used to group
// documents on the Documents tab. Admins add, rename and delete.
import React, { useState } from 'react';
import { useApi } from '../../../lib/api';
import { useDocumentCategories } from './documentHelpers';

export default function DocumentCategoriesSettings({ appId, canWrite, cardStyle, sectionHeadStyle, hintStyle }) {
  const api = useApi();
  const { categories, reload } = useDocumentCategories(appId);
  const [newName, setNewName]   = useState('');
  const [editingId, setEditing] = useState(null);
  const [editName, setEditName] = useState('');
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');

  const run = async (fn) => {
    setBusy(true); setError('');
    try { await fn(); reload(); return true; }
    catch (e) { setError(e.response?.data?.error || e.message || 'Something went wrong'); return false; }
    finally { setBusy(false); }
  };

  const add = async () => {
    if (!newName.trim()) return;
    if (await run(() => api.documentCategories.create(appId, { name: newName.trim() }))) setNewName('');
  };
  const rename = async (id) => {
    if (await run(() => api.documentCategories.update(appId, id, { name: editName.trim() }))) setEditing(null);
  };
  const remove = (c) => {
    if (!window.confirm(`Delete the "${c.name}" category?`)) return;
    run(() => api.documentCategories.remove(appId, c.id));
  };

  const rowStyle = {
    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0',
    borderTop: '1px solid var(--border)', fontSize: 13,
  };
  const linkBtn = { background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0 };

  return (
    <div style={cardStyle}>
      <h3 style={sectionHeadStyle}>Document Categories</h3>
      <p style={hintStyle}>
        The types of document shown on the Documents tab, such as HR Policies or Forms &amp; Templates.
        Each document is filed under one of these. A category with documents in it can't be deleted.
      </p>

      <div style={{ marginTop: 14 }}>
        {categories.map(c => (
          <div key={c.id} style={rowStyle}>
            {editingId === c.id ? (
              <>
                <input className="form-input" value={editName} maxLength={60} autoFocus
                  onChange={e => setEditName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') rename(c.id); if (e.key === 'Escape') setEditing(null); }}
                  style={{ flex: 1 }} />
                <button type="button" className="btn-primary" disabled={busy || !editName.trim()} onClick={() => rename(c.id)}>Save</button>
                <button type="button" className="btn-secondary" disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
              </>
            ) : (
              <>
                <span style={{ flex: 1, color: 'var(--text-primary)', fontWeight: 500 }}>{c.name}</span>
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                  {c.document_count} document{c.document_count === 1 ? '' : 's'}
                </span>
                {canWrite && (
                  <>
                    <button type="button" style={{ ...linkBtn, color: 'var(--brand)' }}
                      onClick={() => { setEditing(c.id); setEditName(c.name); setError(''); }}>Rename</button>
                    <button type="button" style={{ ...linkBtn, color: 'var(--status-danger-fg, #e53e3e)' }}
                      disabled={busy} onClick={() => remove(c)}>Delete</button>
                  </>
                )}
              </>
            )}
          </div>
        ))}
        {!categories.length && <p style={hintStyle}>No categories yet.</p>}
      </div>

      {canWrite && (
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <input className="form-input" placeholder="New category, e.g. Forms & Templates" value={newName} maxLength={60}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') add(); }}
            style={{ flex: 1 }} />
          <button type="button" className="btn-primary" disabled={busy || !newName.trim()} onClick={add}>+ Add</button>
        </div>
      )}

      {error && <div className="error-message" style={{ marginTop: 12 }}>{error}</div>}
    </div>
  );
}
